import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Calendar, ChevronDown, ChevronUp, DollarSign, TrendingUp, Wallet } from 'lucide-react';

const formatMoneyCompact = (value) => {
  const n = Number(value || 0);
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}k`;
  return `$${n.toLocaleString()}`;
};

const formatMoneyFull = (value) => `$${Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const formatPercent = (decimal) => `${(Number(decimal || 0) * 100).toFixed(1)}%`;

// Two-segment "where the funds come from" bar — down payment + loan. The
// Polymet brief assumed a richer breakdown (buyer equity / VTB / SBA /
// senior / mezz) but the actual FinancingSnapshot is a simpler model:
// purchase price = down payment + loan amount, with one named lender.
const FundsBar = ({ purchasePrice, downPayment, loanAmount }) => {
  const total = Math.max(purchasePrice, downPayment + loanAmount, 1);
  const downPct = (downPayment / total) * 100;
  const loanPct = (loanAmount / total) * 100;

  return (
    <div className="space-y-3">
      <div className="flex h-3 w-full overflow-hidden rounded-full bg-[hsl(var(--muted))]">
        <div
          className="bg-[hsl(var(--succeedo-orange))]"
          style={{ width: `${downPct}%` }}
          aria-label={`Down payment ${downPct.toFixed(0)}%`}
        />
        <div
          className="bg-[hsl(var(--succeedo-orange))]/40"
          style={{ width: `${loanPct}%` }}
          aria-label={`Loan ${loanPct.toFixed(0)}%`}
        />
      </div>
      <div className="grid grid-cols-2 gap-4 text-[13px]">
        <div className="space-y-0.5">
          <div className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full bg-[hsl(var(--succeedo-orange))]" />
            <span className="text-[hsl(var(--muted-foreground))]">Down payment</span>
          </div>
          <div className="text-[hsl(var(--foreground))] font-medium">
            {formatMoneyFull(downPayment)} <span className="text-[hsl(var(--muted-foreground))] text-[12px]">({downPct.toFixed(0)}%)</span>
          </div>
        </div>
        <div className="space-y-0.5">
          <div className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full bg-[hsl(var(--succeedo-orange))]/40" />
            <span className="text-[hsl(var(--muted-foreground))]">Loan</span>
          </div>
          <div className="text-[hsl(var(--foreground))] font-medium">
            {formatMoneyFull(loanAmount)} <span className="text-[hsl(var(--muted-foreground))] text-[12px]">({loanPct.toFixed(0)}%)</span>
          </div>
        </div>
      </div>
    </div>
  );
};

FundsBar.propTypes = {
  purchasePrice: PropTypes.number.isRequired,
  downPayment: PropTypes.number.isRequired,
  loanAmount: PropTypes.number.isRequired,
};

const StatTile = ({ icon: Icon, label, value, hint }) => (
  <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-[12px] p-4">
    <div className="flex items-center gap-2 mb-2">
      <Icon className="w-4 h-4 text-[hsl(var(--succeedo-orange))]" />
      <div className="font-mono text-[10px] uppercase text-[hsl(var(--muted-foreground))] tracking-wide">
        {label}
      </div>
    </div>
    <div className="text-[24px] font-semibold text-[hsl(var(--foreground))]">{value}</div>
    {hint && (
      <div className="mt-1 text-[11px] text-[hsl(var(--muted-foreground))]">{hint}</div>
    )}
  </div>
);

StatTile.propTypes = {
  icon: PropTypes.elementType.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  hint: PropTypes.string,
};

StatTile.defaultProps = {
  hint: null,
};

const InputRow = ({ label, value }) => (
  <div className="flex items-center justify-between py-2 border-b border-[hsl(var(--border))]/50">
    <span className="text-[13px] text-[hsl(var(--muted-foreground))]">{label}</span>
    <span className="text-[14px] text-[hsl(var(--foreground))] font-medium">{value}</span>
  </div>
);

InputRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

const FinancingSnapshotExpertView = ({ snapshot }) => {
  const [inputsExpanded, setInputsExpanded] = useState(false);

  const inputs = snapshot?.inputs || {};
  const results = snapshot?.results || {};

  // Real shape from FinancingCalculatorPage.handleSaveSnapshot:
  //   inputs: {
  //     purchase_price, down_payment, down_payment_mode, down_payment_percent,
  //     loan_term_years, lender_id, lender_name, interest_rate (decimal),
  //   }
  //   results: {
  //     loan_amount, monthly_payment, total_interest, total_cost,
  //     effective_rate (decimal), amortization_months,
  //   }
  //
  // Fields the original Polymet brief assumed but that aren't actually stored
  // (DSCR, annual_cash_flow, source-of-funds breakdown, year-by-year schedule,
  // sensitivity scenarios) are gracefully omitted instead of rendering as
  // empty / 0.00x panels.
  const purchasePrice = Number(inputs.purchase_price || 0);
  const downPayment = Number(inputs.down_payment || 0);
  const downPaymentPercent = Number(
    inputs.down_payment_percent ||
      (purchasePrice > 0 ? (downPayment / purchasePrice) * 100 : 0),
  );
  const loanAmount = Number(results.loan_amount || (purchasePrice - downPayment) || 0);
  const loanTermYears = Number(inputs.loan_term_years || 0);
  const interestRate = Number(inputs.interest_rate || results.effective_rate || 0);
  const monthlyPayment = Number(results.monthly_payment || 0);
  const totalInterest = Number(results.total_interest || 0);
  const totalCost = Number(results.total_cost || 0);
  const lenderName = inputs.lender_name || snapshot?.lender_name || 'Lender not specified';

  const isAllEquity = !loanAmount || loanAmount === 0;

  if (isAllEquity) {
    return (
      <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-[16px] p-8 text-center">
        <div className="w-16 h-16 rounded-full bg-[hsl(var(--succeedo-orange))]/10 flex items-center justify-center mx-auto mb-4">
          <DollarSign className="w-8 h-8 text-[hsl(var(--succeedo-orange))]" />
        </div>
        <h2 className="text-[20px] font-semibold text-[hsl(var(--foreground))] mb-2">All-Equity Deal</h2>
        <p className="text-[14px] text-[hsl(var(--muted-foreground))] max-w-md mx-auto">
          This acquisition is structured with 100% buyer equity. No debt service required.
        </p>
        <div className="mt-6 pt-6 border-t border-[hsl(var(--border))]">
          <div className="text-[11px] font-mono uppercase text-[hsl(var(--muted-foreground))] mb-1">
            Purchase Price
          </div>
          <div className="text-[24px] font-semibold text-[hsl(var(--foreground))]">
            {formatMoneyFull(purchasePrice)}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Section A — Headline Tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatTile
          icon={DollarSign}
          label="Loan Amount"
          value={formatMoneyCompact(loanAmount)}
        />
        <StatTile
          icon={Calendar}
          label="Term & Rate"
          value={`${loanTermYears}yr · ${formatPercent(interestRate)}`}
        />
        <StatTile
          icon={TrendingUp}
          label="Monthly Payment"
          value={formatMoneyCompact(monthlyPayment)}
          hint={`over ${loanTermYears * 12} months`}
        />
        <StatTile
          icon={Wallet}
          label="Down Payment"
          value={formatMoneyCompact(downPayment)}
          hint={`${downPaymentPercent.toFixed(0)}% of purchase`}
        />
      </div>

      {/* Section B — Funding split */}
      <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-[16px] p-6 space-y-4">
        <div>
          <h2 className="font-serif text-[28px] font-light text-[hsl(var(--foreground))]">Funding Split</h2>
          <p className="text-[12px] text-[hsl(var(--muted-foreground))] mt-1">
            Purchase price {formatMoneyFull(purchasePrice)} = down payment + loan.
          </p>
        </div>
        <FundsBar
          purchasePrice={purchasePrice}
          downPayment={downPayment}
          loanAmount={loanAmount}
        />
      </div>

      {/* Section C — Lender Context */}
      <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-[16px] p-6">
        <h2 className="font-serif text-[28px] font-light text-[hsl(var(--foreground))] mb-3">Lender Context</h2>
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-[13px] text-[hsl(var(--muted-foreground))]">Lender</span>
            <span className="text-[14px] text-[hsl(var(--foreground))] font-medium">{lenderName}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-[13px] text-[hsl(var(--muted-foreground))]">Quoted rate</span>
            <span className="text-[14px] text-[hsl(var(--foreground))] font-medium">{formatPercent(interestRate)}</span>
          </div>
          <p className="text-[12px] text-[hsl(var(--muted-foreground))]/70 pt-2 border-t border-[hsl(var(--border))]">
            Rate and term reflect the model owner&apos;s assumption, not a binding quote.
          </p>
        </div>
      </div>

      {/* Section D — Cost summary */}
      <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-[16px] p-6 space-y-4">
        <h2 className="font-serif text-[28px] font-light text-[hsl(var(--foreground))]">Total Cost of Financing</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="rounded-lg border border-[hsl(var(--border))] p-4">
            <div className="text-[11px] font-mono uppercase text-[hsl(var(--muted-foreground))] mb-1">
              Loan Principal
            </div>
            <div className="font-serif text-[28px] font-light text-[hsl(var(--foreground))]">
              {formatMoneyFull(loanAmount)}
            </div>
          </div>
          <div className="rounded-lg border border-[hsl(var(--border))] p-4">
            <div className="text-[11px] font-mono uppercase text-[hsl(var(--muted-foreground))] mb-1">
              Total Interest
            </div>
            <div className="font-serif text-[28px] font-light text-[hsl(var(--foreground))]">
              {formatMoneyFull(totalInterest)}
            </div>
          </div>
          <div className="rounded-lg border border-[hsl(var(--succeedo-orange))]/30 bg-[hsl(var(--succeedo-orange))]/5 p-4">
            <div className="text-[11px] font-mono uppercase text-[hsl(var(--muted-foreground))] mb-1">
              Total Cost
            </div>
            <div className="font-serif text-[28px] font-light text-[hsl(var(--foreground))]">
              {formatMoneyFull(totalCost)}
            </div>
          </div>
        </div>
        <p className="text-[11px] text-[hsl(var(--muted-foreground))]/70">
          Total cost = loan principal + total interest, paid over {loanTermYears} years.
        </p>
      </div>

      {/* Section E — Not modeled (transparency) */}
      <div className="bg-[hsl(var(--muted))]/30 border border-dashed border-[hsl(var(--border))] rounded-[16px] p-5 space-y-2">
        <h2 className="text-[14px] font-semibold text-[hsl(var(--foreground))]">Not modeled in this snapshot</h2>
        <ul className="text-[12px] text-[hsl(var(--muted-foreground))] space-y-1 list-disc pl-5">
          <li>DSCR / cash flow coverage — annual cash flow not captured by the calculator.</li>
          <li>Year-by-year amortization schedule — not stored when this snapshot was saved.</li>
          <li>Source-of-funds breakdown beyond down payment vs. loan (no VTB/SBA/mezz).</li>
        </ul>
        <p className="text-[11px] text-[hsl(var(--muted-foreground))]/70">
          A future revision of the Financing Calculator may capture these. For now, expert review can
          rely on the loan economics + lender quote.
        </p>
      </div>

      {/* Section F — Inputs Reference */}
      <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-[16px] p-6">
        <button
          type="button"
          onClick={() => setInputsExpanded(!inputsExpanded)}
          className="flex items-center justify-between w-full text-left"
        >
          <h2 className="font-serif text-[28px] font-light text-[hsl(var(--foreground))]">Inputs Reference</h2>
          {inputsExpanded ? (
            <ChevronUp className="w-5 h-5 text-[hsl(var(--muted-foreground))]" />
          ) : (
            <ChevronDown className="w-5 h-5 text-[hsl(var(--muted-foreground))]" />
          )}
        </button>

        {inputsExpanded && (
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
            <InputRow label="Purchase Price" value={formatMoneyFull(purchasePrice)} />
            <InputRow label="Down Payment ($)" value={formatMoneyFull(downPayment)} />
            <InputRow label="Down Payment (%)" value={`${downPaymentPercent.toFixed(0)}%`} />
            <InputRow label="Loan Term (years)" value={loanTermYears} />
            <InputRow label="Interest Rate" value={formatPercent(interestRate)} />
            <InputRow label="Lender" value={lenderName} />
            <InputRow label="Loan Amount" value={formatMoneyFull(loanAmount)} />
            <InputRow label="Monthly Payment" value={formatMoneyFull(monthlyPayment)} />
          </div>
        )}
      </div>
    </div>
  );
};

FinancingSnapshotExpertView.propTypes = {
  snapshot: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    label: PropTypes.string,
    preview_metrics: PropTypes.shape({
      primary: PropTypes.string,
      secondary: PropTypes.string,
      tertiary: PropTypes.string,
    }),
    inputs: PropTypes.object,
    results: PropTypes.object,
    lender_name: PropTypes.string,
  }).isRequired,
};

export default FinancingSnapshotExpertView;
